import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import CloseIcon from "../../assets/icons/close-icon";
import AddButton, { DraftButton } from "../buttons/button";

// Rendering an entire large sheet into the DOM stalls the modal, so the preview
// shows a leading slice. The cap is always stated on screen — never silently.
const PREVIEW_ROW_LIMIT = 100;

const cellClass =
  "border-b border-[var(--list-table-border-color)] px-[8px] h-[42px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap";

/**
 * Read-only preview of the picked spreadsheet, shown before the rows are sent.
 *
 * Renders the file exactly as parsed — every column in original header order,
 * no reformatting — so the user can confirm they picked the right file. "Done"
 * is what actually triggers the upload.
 */
const SheetPreviewModal = ({
  open = false,
  onClose = () => {},
  onDone = () => {},
  fileName = "",
  headers = [],
  rows = [],
  uploading = false,
  canUpload = true,
}) => {
  const visibleRows = rows.slice(0, PREVIEW_ROW_LIMIT);
  const isTruncated = rows.length > visibleRows.length;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-[#0000004D]" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="bg-white rounded-lg shadow-xl flex flex-col w-[95vw] max-w-[1200px] max-h-[85vh]">
              {/* Header */}
              <div
                className="h-[61px] bg-[var(--slide-panel-head-bg)] px-4 flex items-center justify-between border-b flex-shrink-0 rounded-t-lg"
                style={{ borderColor: "var(--slide-panel-border)" }}
              >
                <div className="flex flex-col min-w-0">
                  <Dialog.Title
                    style={{
                      color: "var(--slide-panel-h-fg)",
                      fontSize: "var(--slide-panel-h-fs)",
                      fontWeight: "var(--slide-panel-h-fw)",
                    }}
                    className="truncate"
                  >
                    Preview — {fileName}
                  </Dialog.Title>
                  <span className="text-[length:--list-table-row-font-size] text-[var(--dashboard-text-secondary)]">
                    {rows.length} rows
                    {isTruncated
                      ? ` · showing first ${visibleRows.length}`
                      : ""}
                  </span>
                </div>
                <button type="button" onClick={onClose} aria-label="Close preview">
                  <CloseIcon />
                </button>
              </div>

              {/* Sheet */}
              <div className="flex-1 overflow-auto px-5 py-4">
                <div className="outline outline-[var(--list-table-border-color)] outline-1 border-radius overflow-auto">
                  <table className="w-full border-collapse mt-0">
                    <thead className="sticky top-0 bg-[var(--list-table-heading-bg)] z-[10]">
                      <tr className="h-[42px]">
                        <th
                          style={{ minWidth: 56 }}
                          className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]"
                        >
                          ROW
                        </th>
                        {headers.map((header) => (
                          <th
                            key={header}
                            style={{ minWidth: 140 }}
                            className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]"
                          >
                            {String(header).toUpperCase()}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={headers.length + 1}
                            className="px-[8px] h-[42px] text-center text-[var(--list-table-row-fg-color)] text-[length:--list-table-row-font-size]"
                          >
                            Nothing to preview.
                          </td>
                        </tr>
                      ) : (
                        visibleRows.map((row, index) => (
                          <tr
                            key={index}
                            className="hover:bg-[var(--list-table-row-hover-bg)] bg-[var(--list-table-row-bg)]"
                          >
                            <td
                              className={`${cellClass} text-[var(--dashboard-text-tertiary)]`}
                            >
                              {index + 1}
                            </td>
                            {headers.map((header) => (
                              <td key={header} className={cellClass}>
                                {row?.[header] === "" ||
                                row?.[header] === null ||
                                row?.[header] === undefined
                                  ? "--"
                                  : String(row[header])}
                              </td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {isTruncated && (
                  <p className="mt-3 text-[length:--list-table-row-font-size] text-[var(--dashboard-text-tertiary)]">
                    Preview limited to the first {visibleRows.length} rows. All{" "}
                    {rows.length} rows will be uploaded.
                  </p>
                )}
              </div>

              {/* Footer */}
              <div
                className="flex justify-between items-center px-5 py-4 border-t flex-shrink-0"
                style={{ borderColor: "var(--slide-panel-border)" }}
              >
                <DraftButton text="Cancel" onclkFn={onClose} />
                {canUpload && (
                  <AddButton
                    prefixIcon={false}
                    text="Done"
                    loading={uploading}
                    onclkFn={onDone}
                  />
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SheetPreviewModal;
